import { isConfigEnabled } from './isConfigEnabled';

const DEFAULT_NEGOTIATION_OPTIONS = [
    'Deal Review',
    'New Negotiation',
    'Negotiation In Progress',
    'Negotiation Completed',
    'Negotiation Cancelled',
];

const DEFAULT_TIER_APPROVAL_DATE_FIELDS = {
    0: 'tier0ApprovalDate',
    1: 'tier1ApprovalDate',
    2: 'tier2ApprovalDate',
};

/**
 * Collects block.config sections from nested blocks/subBlocks.
 */
const collectFromBlocks = (blocks, pickConfig) => {
    const found = [];

    const collect = (block) => {
        const picked = pickConfig(block?.config);
        if (picked) {
            found.push(picked);
        }
    };

    if (!blocks?.length) {
        return found;
    }

    blocks.forEach((block) => {
        if (block.subBlock?.length) {
            block.subBlock.forEach(collect);
        } else {
            collect(block);
        }
    });

    return found;
};

/**
 * Returns the richest statusTransition config from blocks (prefers deriveRules).
 */
export const getStatusTransitionConfigFromBlocks = (blocks) => {
    const configs = collectFromBlocks(blocks, (cfg) => {
        if (!cfg?.statusTransition || typeof cfg.statusTransition !== 'object') {
            return null;
        }

        const statusTransition = cfg.statusTransition;
        if (!statusTransition.statusField || !Array.isArray(statusTransition.transitions)) {
            return null;
        }

        return statusTransition;
    });

    if (!configs.length) {
        return null;
    }

    return configs.find((cfg) => Array.isArray(cfg.deriveRules) && cfg.deriveRules.length > 0) || configs[0];
};

/**
 * Returns trackerThreshold config from blocks (Overview block).
 */
export const getTrackerThresholdConfigFromBlocks = (blocks) => {
    const configs = collectFromBlocks(blocks, (cfg) => (
        isConfigEnabled(cfg?.trackerThreshold, true) && Array.isArray(cfg?.trackerThreshold?.rules)
            && cfg.trackerThreshold.rules.length > 0
            ? cfg.trackerThreshold
            : null
    ));

    return configs[0] || null;
};

/**
 * Maps block.config.statusTransition to negotiation status runtime config.
 */
export const resolveNegotiationConfigFromStatusTransition = (statusTransition) => {
    if (!statusTransition) {
        return null;
    }

    const statusField = statusTransition.statusField || 'negotiationStatus';
    const tierApprovalDateFields = statusTransition.tierApprovalDateFields || DEFAULT_TIER_APPROVAL_DATE_FIELDS;

    const deriveRules = (statusTransition.deriveRules || []).map((rule) => ({
        ...rule,
        isEnable: rule.isEnable !== false,
        condition: rule.when || rule.condition,
        set: rule.set,
    }));

    return {
        isEnable: true,
        field: statusField,
        approvalDateField: 'approvalDate',
        topTierField: 'topTierRequired',
        options: statusTransition.options || DEFAULT_NEGOTIATION_OPTIONS,
        onControlFieldCleared: statusTransition.onControlFieldCleared || 'Deal Review',
        skipWhenCurrentValueIn: statusTransition.skipWhenCurrentValueIn || ['Negotiation Cancelled'],
        tierApprovalDateFields,
        autoUpdate: {
            isEnable: deriveRules.length > 0,
            skipWhenCurrentValueIn: statusTransition.skipWhenCurrentValueIn || ['Negotiation Cancelled'],
            skipWhenSavingField: statusField,
            tierApprovalDateFields,
            triggerFields: {
                always: ['approvalDate', 'topTierRequired', 'negotiationCompletionDate'],
                includeRelevantTierApprovalDate: true,
            },
            rules: deriveRules,
        },
        transitions: statusTransition.transitions || [],
    };
};

/**
 * Resolves negotiation dropdown options from element config or statusTransition.
 */
export const resolveNegotiationStatusOptions = (statusTransition, elementOptions) => {
    if (Array.isArray(elementOptions) && elementOptions.length > 0) {
        return elementOptions.map((option) => (
            typeof option === 'string'
                ? { label: option, value: option }
                : { label: option.label, value: option.value }
        ));
    }

    const options = statusTransition?.options || DEFAULT_NEGOTIATION_OPTIONS;
    return options.map((status) => ({ label: status, value: status }));
};
